import React from 'react';
import { cn } from '../../utils/cn';
import { useThemeColors } from '../../providers/ThemeProvider';

interface TimeInputProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  placeholder?: string;
  className?: string;
}

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      value,
      onChange,
      disabled = false,
      error = false,
      fullWidth = true,
      size = 'md',
      placeholder,
      className,
    },
    ref,
  ) => {
    const colors = useThemeColors();

    // Match DatePicker's minHeight
    const minHeightStyle = {
      xs: '22px',
      sm: '30px',
      md: '36px',
      lg: '42px',
    }[size];

    // Convert Date to time string (HH:MM)
    const dateToTimeString = (date: Date | null): string => {
      if (date == null) {
        return '';
      }

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${hours}:${minutes}`;
    };

    // Convert time string (HH:MM) to Date
    const timeStringToDate = (timeString: string, baseDate: Date | null): Date | null => {
      if (timeString == null || timeString === '') {
        return null;
      }

      const [hoursStr, minutesStr] = timeString.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (isNaN(hours) || isNaN(minutes)) {
        return null;
      }

      // Use baseDate if provided, otherwise create a new date
      const date = baseDate != null ? new Date(baseDate) : new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      date.setSeconds(0);
      date.setMilliseconds(0);

      return date;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const timeString = e.target.value;
      const newDate = timeStringToDate(timeString, value);
      onChange(newDate);
    };

    return (
      <>
        {/* Add global styles to hide native time picker icon and normalize sizing */}
        <style>
          {`
            input[type="time"]::-webkit-calendar-picker-indicator {
              display: none;
              -webkit-appearance: none;
            }
            input[type="time"]::-webkit-inner-spin-button,
            input[type="time"]::-webkit-outer-spin-button {
              display: none;
              -webkit-appearance: none;
            }
            input[type="time"] {
              line-height: 1.25rem;
              height: auto;
            }
          `}
        </style>
        <input
          ref={ref}
          type="time"
          value={dateToTimeString(value)}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          style={{ minHeight: minHeightStyle, height: minHeightStyle }}
          className={cn(
            // Base styling to match DatePicker component exactly
            'block text-left',
            fullWidth && 'w-full',

            // Match DatePicker's hardcoded padding/size (not using sizeClasses variable)
            'px-2 py-2 text-xs',

            // Base styles
            'border rounded-md shadow-sm transition-colors duration-200',
            colors.classes.textPrimary,
            colors.classes.bgPrimary,
            'placeholder-gray-400 dark:placeholder-gray-400',

            // State-based styling
            error
              ? [
                  colors.classes.borderError,
                  'focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:focus:border-red-400',
                ]
              : [
                  colors.classes.borderDefault,
                  'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none',
                ],

            // Disabled state
            disabled &&
              cn(
                colors.classes.bgSecondary,
                'cursor-not-allowed opacity-75',
                colors.classes.borderDisabled,
              ),

            // User's custom classes
            className,
          )}
        />
      </>
    );
  },
);

TimeInput.displayName = 'TimeInput';
